import { isApiError } from '@shigo-ui/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  AutocompleteMultiSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardApiErrorMessage } from '../../../../../lib/apiErrorMessages';
import { dashboardT } from '../../../../../lib/messages';
import { useReportShareUserOptions } from '../../../savedReports/hooks';

const saveReportFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, {
      error: () => dashboardT('reportExplorer.save.nameRequired'),
    }),
  sharedWith: z.array(z.string()),
});

type SaveReportFormValue = z.infer<typeof saveReportFormSchema>;

interface SaveReportDialogProps {
  agencyId?: string | undefined;
  initialName: string;
  initialSharedWith: string[];
  pending: boolean;
  onSubmit: (name: string, sharedWith: string[]) => Promise<void>;
  onClose: () => void;
}

export function SaveReportDialog({
  agencyId,
  initialName,
  initialSharedWith,
  pending,
  onSubmit,
  onClose,
}: SaveReportDialogProps) {
  'use no memo';

  const guard = useBusyDialogGuard(pending, onClose);
  const form = useForm<SaveReportFormValue>({
    defaultValues: { name: initialName, sharedWith: initialSharedWith },
    resolver: zodResolver(saveReportFormSchema),
  });
  const usersQuery = useReportShareUserOptions(true, agencyId);
  const error = form.formState.errors.name?.message ?? form.formState.errors.root?.message;

  async function submit(values: SaveReportFormValue) {
    try {
      await onSubmit(values.name, values.sharedWith);
    } catch (error) {
      showRequestError(error, dashboardT('reportExplorer.save.error'));
      form.setError('root', {
        message: isApiError(error)
          ? dashboardApiErrorMessage(error)
          : dashboardT('reportExplorer.save.error'),
      });
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-base">{dashboardT('reportExplorer.save.title')}</DialogTitle>
          <DialogDescription>{dashboardT('reportExplorer.save.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            <div className="space-y-5 px-5 py-5">
              <div className="space-y-1.5">
                <Label htmlFor="report-name" className="text-[13px] font-semibold">
                  {dashboardT('reportExplorer.save.name')}{' '}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="report-name"
                  required
                  aria-invalid={Boolean(error)}
                  {...form.register('name')}
                />
                {error ? (
                  <p className="text-destructive text-xs" role="alert">
                    {error}
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">
                  {dashboardT('reportExplorer.save.sharedWith')}
                </Label>
                <Controller
                  control={form.control}
                  name="sharedWith"
                  render={({ field }) => (
                    <AutocompleteMultiSelect
                      label={dashboardT('reportExplorer.save.sharedWith')}
                      options={usersQuery.data ?? []}
                      value={field.value}
                      loading={usersQuery.isFetching}
                      disabled={pending}
                      placeholder={dashboardT('reportExplorer.save.searchUsers')}
                      emptyMessage={dashboardT(
                        usersQuery.isError
                          ? 'reportExplorer.save.usersError'
                          : 'reportExplorer.save.noUsers',
                      )}
                      onValueChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={guard.close}>
                {dashboardT('reportExplorer.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {dashboardT(pending ? 'reportExplorer.save.saving' : 'reportExplorer.save.action')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
